// src/audio/speechManager.ts — Multisensory Human Speech Engine for Spelling & Phonetics (FR-ENG-004)
// Upgrades legacy mechanical synthesizer to pleasant, natural human British voices,
// with dedicated female voice matching, direct browser voice selection,
// and custom rate/pitch calibration for ADHD multisensory processing.

export interface VoicePersona {
  id: string;
  label: string;
  icon: string;
  description: string;
  preferredLang: string;
  nameKeywords: string[];
  rate: number;
  pitch: number;
  samplePhrase: string;
}

export const VOICE_PERSONAS: VoicePersona[] = [
  {
    id: 'crisp_phonics',
    label: 'Pleasant Phonics Coach',
    icon: '🌸',
    description: 'Pleasant, natural British woman’s voice with clear phonemic articulation',
    preferredLang: 'en-GB',
    nameKeywords: [
      'google uk english female',
      'sonia',
      'libby',
      'maisie',
      'susan',
      'hazel',
      'female',
      'google us english',
      'natural',
      'online',
      'neural',
    ],
    rate: 0.92,
    pitch: 1.0,
    samplePhrase: 'Splendid! Listen carefully and spell each word.',
  },
];

const STORAGE_KEY_PERSONA = 'clawey_preferred_voice_persona';
const STORAGE_KEY_VOICE_NAME = 'clawey_preferred_voice_name';
const STORAGE_KEY_RATE = 'clawey_preferred_speech_rate';

export class SpeechManager {
  private static cachedVoices: SpeechSynthesisVoice[] = [];
  private static isInitialized = false;
  private static listeners: Array<() => void> = [];

  /** Initialize voice list listeners */
  static init(): void {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    if (this.isInitialized) return;
    this.isInitialized = true;

    const loadVoices = () => {
      try {
        const voices = window.speechSynthesis.getVoices();
        if (voices && voices.length > 0) {
          this.cachedVoices = voices;
          this.notifyListeners();
        }
      } catch {
        // Fallback for mock environments
      }
    };

    loadVoices();
    try {
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    } catch {
      // Browser compatibility
    }
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }

  /** Subscribe to voiceschanged events */
  static onVoicesChanged(cb: () => void): () => void {
    this.init();
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb);
    };
  }

  private static notifyListeners(): void {
    for (const cb of this.listeners) {
      try {
        cb();
      } catch {
        // Ignore errors in callbacks
      }
    }
  }

  /** Get all available browser voices */
  static getAvailableVoices(): SpeechSynthesisVoice[] {
    this.init();
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const fresh = window.speechSynthesis.getVoices();
      if (fresh && fresh.length > 0) {
        this.cachedVoices = fresh;
      }
    }
    return this.cachedVoices;
  }

  /** Get user's preferred voice name from storage */
  static getSelectedVoiceName(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEY_VOICE_NAME);
    } catch {
      return null;
    }
  }

  /** Save user's selected voice name */
  static setSelectedVoiceName(name: string): void {
    try {
      localStorage.setItem(STORAGE_KEY_VOICE_NAME, name);
    } catch {
      // Ignored
    }
  }

  /**
   * Score a voice for pleasant natural human female qualities
   */
  static scoreVoice(v: SpeechSynthesisVoice): number {
    const name = v.name.toLowerCase();
    const lang = v.lang.toLowerCase().replace('_', '-');
    let score = 0;

    // Reject non-English
    if (!lang.startsWith('en')) return -1000;

    // British English priority for KS2 11+
    if (lang.startsWith('en-gb')) {
      score += 80;
    } else if (lang.startsWith('en-us') || lang.startsWith('en-ca') || lang.startsWith('en-au')) {
      score += 30;
    }

    // Natural / Neural online voices
    if (name.includes('natural') || name.includes('online') || name.includes('neural')) {
      score += 60;
    }

    // Pleasant female keywords
    const femaleKeywords = [
      'female', 'woman', 'sonia', 'libby', 'maisie', 'jenny', 'aria',
      'susan', 'hazel', 'zira', 'serena', 'stephanie', 'victoria',
      'karen', 'moira', 'samantha', 'fiona'
    ];
    if (femaleKeywords.some((kw) => name.includes(kw))) {
      score += 100;
    }

    // Premier pleasant British female voices
    if (name.includes('google uk english female')) score += 200;
    if (name.includes('sonia')) score += 180;
    if (name.includes('libby')) score += 170;
    if (name.includes('maisie')) score += 160;
    if (name.includes('google us english')) score += 120;
    if (name.includes('susan')) score += 90;
    if (name.includes('hazel')) score += 80;
    if (name.includes('zira')) score += 70;

    // Heavily penalize robotic / male desktop voices
    if (name.includes('david')) score -= 150;
    if (name.includes('mark')) score -= 150;
    if (name.includes('george')) score -= 100;
    if (name.includes('desktop')) score -= 50;
    if (name.includes('male') && !name.includes('female')) score -= 150;

    return score;
  }

  /** Find the best matching browser voice for a given persona or user preference */
  static resolveVoiceForPersona(_persona?: VoicePersona): SpeechSynthesisVoice | null {
    const voices = this.getAvailableVoices();
    if (!voices || voices.length === 0) return null;

    // 1. If user explicitly selected a voice by name that exists
    const preferredName = this.getSelectedVoiceName();
    if (preferredName) {
      const matched = voices.find((v) => v.name === preferredName);
      if (matched) return matched;
    }

    // 2. Score and pick the best pleasant female voice
    const ranked = [...voices]
      .filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith('en'))
      .sort((a, b) => this.scoreVoice(b) - this.scoreVoice(a));

    if (ranked.length > 0 && this.scoreVoice(ranked[0]!) > 0) {
      return ranked[0]!;
    }

    // Fallback: any en-GB voice
    const gbVoices = voices.filter((v) => v.lang.toLowerCase().replace('_', '-').startsWith('en-gb'));
    if (gbVoices.length > 0) return gbVoices[0] || null;

    // Fallback: any English voice
    const enVoices = voices.filter((v) => v.lang.toLowerCase().startsWith('en'));
    if (enVoices.length > 0) return enVoices[0] || null;

    return voices[0] || null;
  }

  /** Get current active persona ID */
  static getActivePersonaId(): string {
    return 'crisp_phonics';
  }

  /** Save active persona preference */
  static setActivePersonaId(personaId: string): void {
    try {
      localStorage.setItem(STORAGE_KEY_PERSONA, personaId);
    } catch {
      // Ignored
    }
  }

  /** Get user's custom speed multiplier (default 0.92) */
  static getSpeedMultiplier(): number {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_RATE);
      return raw ? parseFloat(raw) : 0.92;
    } catch {
      return 0.92;
    }
  }

  /** Set user's speed multiplier */
  static setSpeedMultiplier(mult: number): void {
    try {
      localStorage.setItem(STORAGE_KEY_RATE, String(mult));
    } catch {
      // Ignored
    }
  }

  /** Get the active persona definition */
  static getActivePersona(): VoicePersona {
    return VOICE_PERSONAS[0]!;
  }

  /**
   * Speak a word or phrase using the human voice engine
   */
  static speak(
    text: string,
    options?: {
      personaId?: string;
      customRate?: number;
      customPitch?: number;
      onEnd?: () => void;
    }
  ): void {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    this.init();
    window.speechSynthesis.cancel(); // Stop any pending utterance to avoid robotic overlap

    const persona = this.getActivePersona();
    const utterance = new SpeechSynthesisUtterance(text);
    const resolvedVoice = this.resolveVoiceForPersona(persona);

    if (resolvedVoice) {
      utterance.voice = resolvedVoice;
      utterance.lang = resolvedVoice.lang;
    } else {
      utterance.lang = persona.preferredLang;
    }

    const speedMult = this.getSpeedMultiplier();
    utterance.rate = (options?.customRate ?? persona.rate) * (speedMult / 0.92);
    utterance.pitch = options?.customPitch ?? persona.pitch;

    if (options?.onEnd) {
      utterance.onend = options.onEnd;
    }

    window.speechSynthesis.speak(utterance);
  }

  /** Test the current voice */
  static testPersona(onEnd?: () => void): void {
    const persona = this.getActivePersona();
    this.speak(persona.samplePhrase, { onEnd });
  }
}
